import { Box } from "@mui/material";
import { memo } from "react";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import ArtifactCardList from "src/views/assistant/components/Artifacts/ArtifactCardList";
import ArtifactItemList from "src/views/assistant/components/Artifacts/ArtifactItemList";
import ArtifactsHeader from "src/views/assistant/components/Artifacts/ArtifactsHeader";
import { useUIStore } from "src/views/assistant/stores/UIStore";

interface ArtifactsTabProps {
  artifacts?: Record<string, any>;
}

/**
 * ArtifactsTab - Reusable wrapper for artifacts display
 *
 * Features:
 * - Can accept artifacts as props OR use internal hook
 * - Uses UIStore for list/detail view state
 * - Provides consistent interface across products
 */
const ArtifactsTab = memo(({ artifacts }: ArtifactsTabProps) => {
  const { rightSidebar } = useUIStore();

  const isArtifactItemOpen = rightSidebar.isArtifactItemOpen;
  const currentArtifactId = rightSidebar.currentArtifactId;

  // If no artifacts provided, use empty object for backward compatibility
  const displayArtifacts = artifacts || {};

  // Compute artifactList once
  const artifactList = Array.isArray(displayArtifacts)
    ? displayArtifacts
    : Object.values(displayArtifacts);

  // Find current artifact if needed
  const currentArtifact = currentArtifactId
    ? typeof displayArtifacts === "object" && !Array.isArray(displayArtifacts)
      ? displayArtifacts[currentArtifactId]
      : artifactList.find((a: any) => a.id === currentArtifactId)
    : undefined;

  return (
    <Box data-testid="aa-artifact-views-container">
      {isArtifactItemOpen && (
        <ArtifactsHeader currentArtifact={currentArtifact} />
      )}
      <SideBarScroll>
        {!artifactList.length && (
          <Box data-testid="aa-artifact-views-empty">
            <NoContentCard
              title="No Artifacts found yet"
              description="Artifacts will show up here after running a function"
            />
          </Box>
        )}
        {!isArtifactItemOpen && (
          <Box data-testid="aa-artifact-views-list" sx={{ px: 2, pt: 0.5 }}>
            <ArtifactCardList artifacts={artifactList} />
          </Box>
        )}
        {isArtifactItemOpen && (
          <Box data-testid="aa-artifact-views-detail">
            <ArtifactItemList artifacts={artifactList} />
          </Box>
        )}
      </SideBarScroll>
    </Box>
  );
});

ArtifactsTab.displayName = "ArtifactsTab";

export default ArtifactsTab;
